import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box, Badge, Text, Tooltip } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import { dash, formatDateTime, STATUS_COLORS } from '../one-on-ones/OneOnOneCellRenderers';
import { ONE_ON_ONE_HISTORY_EVENT_COLORS, ONE_ON_ONE_HISTORY_EVENT_HELP } from './oneOnOneHistoryColumns';
import type { OneOnOneHistoryDisplayRow } from './useDataLibraryOneOnOneHistory';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

const DATE_COLUMNS = new Set(['firstScheduledStart', 'fromStart', 'toStart', 'createdAt', 'currentStart']);

function renderCell(row: OneOnOneHistoryDisplayRow, columnId: string): React.ReactNode {
  const value = (row as unknown as Record<string, unknown>)[columnId];
  if (value == null || value === '') return dash;
  if (DATE_COLUMNS.has(columnId)) return <Text size="sm" c="dimmed">{formatDateTime(String(value))}</Text>;
  if (columnId === 'event') {
    return (
      <Tooltip label={ONE_ON_ONE_HISTORY_EVENT_HELP[row.eventCode]} disabled={!ONE_ON_ONE_HISTORY_EVENT_HELP[row.eventCode]} withArrow multiline w={260}>
        <Badge
          size="sm"
          variant="light"
          color={ONE_ON_ONE_HISTORY_EVENT_COLORS[row.eventCode] ?? 'gray'}
          styles={{ label: { textTransform: 'none' } }}
        >
          {row.event}
        </Badge>
      </Tooltip>
    );
  }
  if (columnId === 'status') {
    return (
      <Badge size="sm" variant="light" color={STATUS_COLORS[row.status] ?? 'gray'} styles={{ label: { textTransform: 'none' } }}>
        {row.status}
      </Badge>
    );
  }
  return <Text size="sm" fw={columnId === 'participantName' ? 500 : undefined}>{String(value)}</Text>;
}

interface OneOnOneHistoryTableProps {
  data: OneOnOneHistoryDisplayRow[];
  groupedData: GroupNode<OneOnOneHistoryDisplayRow>[] | null;
  columns: ColumnConfig[];
  sortConfig: SortConfig;
  onSort: () => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const colStyle: React.CSSProperties = { minWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden' };

export function OneOnOneHistoryTable({
  data,
  groupedData,
  columns,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: OneOnOneHistoryTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderRow = (row: OneOnOneHistoryDisplayRow) => (
    <Table.Tr key={row.eventId} className={classes.tableRow}>
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={colStyle}
          width={layout.columnWidths[col.id]}
        >
          {renderCell(row, col.id)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );

  const showEmpty = !loading && data.length === 0;

  return (
    <Card withBorder p={0} radius={8} bg="white" style={{ boxShadow: 'none', position: 'relative' }} className={classes.tableCard}>
      <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              {columns.map((col, index) => (
                <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={colStyle} layout={layout}>
                  {col.sortable ? (
                    <SortableTableHeader label={col.label} sortKey={col.id} currentSort={sortConfig} onSort={onSort} />
                  ) : (
                    <NonSortableColumnHeader label={col.label} />
                  )}
                </ResizableHeaderCell>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groupedData
              ? flattenGroups(groupedData, collapsedGroups).map(item =>
                  'group' in item ? (
                    <CollapsibleGroupHeader
                      key={`group-${item.group.key}`}
                      label={item.group.label}
                      count={item.group.rows.length}
                      depth={item.group.depth}
                      colSpan={columns.length}
                      isCollapsed={collapsedGroups.has(item.group.key)}
                      onToggle={() => toggleGroup(item.group.key)}
                      entityLabel={item.group.rows.length === 1 ? 'change' : 'changes'}
                    />
                  ) : (
                    renderRow(item.row)
                  ),
                )
              : data.map(renderRow)}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {showEmpty && (
        <DataLibraryEmptyState message="No 1:1 changes in these cohorts" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />
      )}
      {!groupedData && totalCount > 0 && (
        <Box className={classes.paginationWrapper}>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[25, 50, 100, 250]}
            entityLabel="changes"
          />
        </Box>
      )}
    </Card>
  );
}
